import { useEffect, useMemo, useRef, useState } from "react";

import type { GatewayError, Module } from "@eisox/api/core-api";
import { ActionButton, Button, cn, Dialog, Heading, ScrollArea, Typography } from "@eisox/design-system";
import { ArrowRightIcon, BracesIcon, NutIcon, WarningIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { Link } from "@tanstack/react-router";

import { InitDataDialog, VariableDetailsContent } from "~/features/module";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import {
  formatError,
  getLiveErrorVariableDataPermissions,
  shouldAppendLiveErrorSnapshot,
  type ErrorMessageFormated,
} from "../helpers";

const NAME = "LiveErrorDialog";

interface LiveErrorDialogProps extends Dialog.RootProps {
  errors: GatewayError[];
  module: Module;
  airHandlerUnit?: { name: string };
  boilerRoom?: { name: string };
}

export const LiveErrorDialog: React.FC<LiveErrorDialogProps> = ({
  errors,
  module,
  airHandlerUnit,
  boilerRoom,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen,
  children,
  ...props
}) => {
  const { building, permissions } = useBuildingContext(NAME);
  const variableDataPermissions = getLiveErrorVariableDataPermissions(permissions, { boilerRoom, airHandlerUnit });

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const [displayVariables, setDisplayVariables] = useState(false);
  const [errorMessages, setErrorMessages] = useState<ErrorMessageFormated[]>([]);

  const prevErrorsRef = useRef<ErrorMessageFormated | undefined>(undefined);

  const error = useMemo(
    () => formatError({ errors, module, boilerRoom, airHandlerUnit }),
    [errors, module, boilerRoom, airHandlerUnit],
  );

  useEffect(() => {
    if (!open) {
      prevErrorsRef.current = undefined;
      setErrorMessages(prev => (prev.length === 0 ? prev : []));
      setDisplayVariables(false);
      return;
    }

    const prevErrors = prevErrorsRef.current?.errors;
    if (!shouldAppendLiveErrorSnapshot(prevErrors, error.errors)) return;

    prevErrorsRef.current = error;
    setErrorMessages(prev => [error, ...prev]);
  }, [error, open]);

  return (
    <Dialog.Root {...props} open={open} onOpenChange={setOpen}>
      {children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
      <Dialog.Content
        className="min-w-90 flex h-[90dvh] w-fit max-w-[85vw] flex-col items-center"
        title={m.live_error_dialog_title()}
        icon={<WarningIcon color={colors.primary.red} />}
      >
        <div className="gap-10 min-h-0 flex h-full flex-row overflow-x-auto">
          <div className="gap-10 min-w-87.5 flex flex-col">
            <div className="gap-2.5 flex flex-col">
              <Heading as="h4" size={3}>
                {m.actions()}
              </Heading>
              {permissions.module?.read && (
                <Button asChild className={cn("text-base text-primary-dark-blue")}>
                  <Link to={"/buildings/$buildingId/settings/bms/modules"} params={{ buildingId: building.id }}>
                    <NutIcon />
                    {m.bms_module_management()}
                    <ArrowRightIcon />
                  </Link>
                </Button>
              )}
              {variableDataPermissions?.update && (
                <InitDataDialog buildingId={building.id} moduleId={module.id}>
                  <Button className="text-base text-primary-dark-blue">
                    <BracesIcon />
                    {m.init_data_by_bms_module()}
                    <ArrowRightIcon />
                  </Button>
                </InitDataDialog>
              )}
              {variableDataPermissions?.read && (
                <Button
                  className={cn("text-base", displayVariables ? "text-primary-blue" : "text-primary-dark-blue")}
                  onClick={() => setDisplayVariables(!displayVariables)}
                >
                  <BracesIcon />
                  {m.variables_details()}
                  <ArrowRightIcon />
                </Button>
              )}
            </div>
            <ScrollArea orientation="both" className={cn("w-full text-base")}>
              {errorMessages.map(({ date, errors }, index) => (
                <div key={index} className={cn("mb-5 gap-2.5 flex flex-col")}>
                  <p className={cn("font-medium italic")}>{date}</p>
                  {errors.map(({ name, ip, messages }, j) => (
                    <div key={j}>
                      <Typography className="font-medium max-w-125 italic">
                        {ip} {name && `(${name})`}
                      </Typography>
                      <p className="text-primary-dark-gray">{`"${messages.msg}","type":"${messages.type}"`}</p>
                    </div>
                  ))}
                  {!errors.length && <p>{m.live_error_dialog_no_error()}</p>}
                </div>
              ))}
            </ScrollArea>
          </div>
          {displayVariables && (
            <div className="flex min-w-[45vw]">
              <VariableDetailsContent buildingId={building.id} moduleId={module.id} />
            </div>
          )}
        </div>
        <Dialog.Close asChild>
          <ActionButton variant="cancel">{m.close()}</ActionButton>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
};

LiveErrorDialog.displayName = NAME;
